import React, { ChangeEvent, useMemo } from 'react';
import { Input, Upload, Button, Icon, Typography, message } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { downloadCSV } from 'src/utils/csvUtils';
import styles from './LookupOptions.module.less';

const { TextArea } = Input;
const { Text } = Typography;

type Props = {
  onChange: (value: string) => void;
  currentValue: string;
};

const SEPARATOR = '\n';

const CSV_NAME = 'sku-template';

const CSV_TEMPLATE = `
SKU
Enter 1 unique SKU per row. Column header is not mandatory.
`;

export const MAX_LENGTH = 10_000;

const MAX_LENGTH_LABEL = MAX_LENGTH.toLocaleString('en-US');

const split = (str: string) => str.split(/\s+/).filter(Boolean);

const clean = (str: string) => str.replace(/^\s*(SKU\b|Enter).*$/gim, '');

const downloadSkusTemplate = () => {
  downloadCSV(CSV_TEMPLATE.trim(), CSV_NAME);
};

export const Skus = ({ onChange, currentValue }: Props) => {
  const currentSkusCount = useMemo(() => split(currentValue).length, [currentValue]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const beforeUpload = (currentFile: RcFile, fileList: RcFile[]) => {
    if (currentFile === fileList[0]) {
      const csvFiles = fileList.filter((file) => file.type === 'text/csv');
      if (csvFiles.length < fileList.length) {
        message.error('The uploaded file format is not supported. Please utilize a .csv file.');
      }

      const blobFiles = new Blob(
        csvFiles.flatMap((file) => [file, SEPARATOR]),
        { type: 'text/csv' }
      );

      blobFiles.text().then((values: string) => {
        const prevSkus = split(currentValue);
        const newSkus = split(clean(values));
        const uniqSkus = [...new Set([...prevSkus, ...newSkus])];

        if (uniqSkus.length > MAX_LENGTH) {
          uniqSkus.splice(MAX_LENGTH);
          const addedSkusCount = uniqSkus.length - prevSkus.length;

          message.warn(
            `There is a limit of ${MAX_LENGTH_LABEL} unique SKUs. The first ${addedSkusCount} rows were inserted and the rest were ignored.`
          );
        } else {
          const addedSkusCount = uniqSkus.length - prevSkus.length;

          if (newSkus.length > 0) {
            message.success(`The ${addedSkusCount} skus have been added.`);
          }
        }
        onChange(uniqSkus.join(SEPARATOR));
      });
    }

    return false;
  };

  return (
    <>
      <label>Please enter SKUs in separate rows.</label>
      <div className={styles.subLabel}>
        <span>There is a limit of {MAX_LENGTH_LABEL} unique SKUs (one per line)</span>
        <span>
          {currentSkusCount.toLocaleString('en-US')}/{MAX_LENGTH_LABEL}
        </span>
        {currentSkusCount > MAX_LENGTH && (
          <Text type="warning" className={styles.warnLabel}>
            The first {MAX_LENGTH_LABEL} rows were inserted and the rest were ignored.
          </Text>
        )}
      </div>
      <TextArea onChange={handleChange} rows={8} value={currentValue} />
      <p className={styles.helpLabel}>*supported file format (.csv)</p>
      <Upload
        accept=".csv"
        showUploadList={false}
        multiple={true}
        className={styles.uploadBtn}
        beforeUpload={beforeUpload}
      >
        <Button type="primary">
          <Icon type="upload" /> Upload SKUs List
        </Button>
      </Upload>
      <Button type="primary" className={styles.downloadBtn} onClick={downloadSkusTemplate}>
        <Icon type="download" /> Download Template
      </Button>
    </>
  );
};
